@use "@inshared/ui-foundation/css/tokens/breakpoints";
@use "@inshared/ui-foundation/css/tokens/border-width";
@use "@inshared/ui-foundation/css/tokens/border-style";
@use "@inshared/ui-foundation/css/tokens/color";
@use "@inshared/ui-foundation/css/tokens/color/action";
@use "@inshared/ui-foundation/css/tokens/size";
@use "@inshared/ui-foundation/css/tokens/spacing";
@use "@inshared/ui-foundation/css/tokens/typography/font-family";
@use "@inshared/ui-foundation/css/tokens/typography/font-size";
@use "@inshared/ui-foundation/css/tokens/typography/font-weight";
@use "@inshared/ui-foundation/css/tokens/typography/line-height";

:host {
    // Colors
    --background-color: #f6f6f6;
    --overflow-gradient-color: rgba(255, 255, 255, 0.8);
    --divider-color: #{color.$color-border-light};
    --grid-item-color-text: #{color.$color-text};
    --grid-item-color-fill-icon-start: #{color.$color-primary};
    --grid-item-color-heading: #{color.$color-text};

    // Transitions
    --translate-x: 100%;
    --slide-speed: 0.25s;
    --slide-speed-backdrop: 0.1s;
    --menu-translate-x: 100%;

    // Spacing
    --padding-inline: 2rem;
    --close-button-icon-gap: #{spacing.$md};
    --menu-levels-gap: #{spacing.$md-lg};
    --menu-levels-block-start: #{spacing.$md-lg};
    --grid-item-link-gap: #{spacing.$lg};
    --grid-items-margin-block-end: #{spacing.$xxxl};

    // Fonts
    --close-button-font-size: #{font-size.$lg};
    --web-list-font-size: #{font-size.$xl};
    --web-list-font-weight: #{font-weight.$medium};
    --web-list-link-color: #{color.$color-text};
    --web-list-link-hover-color: #{color.$color-secondary};
    --grid-item-link-font-size: #{font-size.$lg};

    // Other
    --overflow-gradient-height: 50px;
    --divider-width: #{border-width.$sm};
    --divider-style: #{border-style.$solid};
    --divider-margin-block: 0;

    &::part(menu-title) {
        font-size: var(--web-list-font-size);
        font-family: #{font-family.$primary-bold};
        color: #{color.$color-text};
        margin: #{spacing.$lg} 0;
    }

    dialog {
        width: 100%;
        height: 100%;
        border: none;
        padding: 0;
        max-width: 100%;
        max-height: 100%;
        animation: slide-out var(--slide-speed) ease-in-out;
    }

    dialog[open] {
        animation: slide-in var(--slide-speed) ease-in-out;
        display: grid;
        grid-template-rows: auto auto 1fr;
        overflow: hidden;
        position: relative;
    }

    dialog[open]::backdrop {
        animation: backdrop-fade-in var(--slide-speed-backdrop) ease-out forwards;
    }

    header {
        background-color: var(--background-color);
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        gap: 1rem;
        padding-inline: var(--padding-inline);

        button {
            background: none;
            border: none;
            padding: spacing.$md;
            min-width: 44px;
            text-align: center;
            margin-left: calc(-1 * spacing.$md);

            &:hover {
                cursor: pointer;
                web-link a {
                    color: #{color.$color-text-secondary-darker};
                    text-decoration: underline;
                }
            }
        }

        button ~ button {
            margin-left: 0;
            margin-right: calc(-1 * spacing.$md);
        }

        .close-button {
            web-icon {
                --icon-height: #{size.$icon-xl};
                --icon-width: #{size.$icon-xl};
            }
        }
    }

    hr {
        height: 1px;
        margin: 0;
        border: none;
        border-top: 1px solid #dadada;
    }

    main {
        background-color: var(--background-color);
        height: 100%;
        overflow: hidden;
        position: relative;

        &::after {
            content: "";
            background: linear-gradient(0deg, var(--overflow-gradient-color), transparent);
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: var(--overflow-gradient-height);
            pointer-events: none;
            z-index: 1;
        }

        .user-name {
            display: flex;
            gap: spacing.$lg;
            margin-top: spacing.$lg;
            justify-content: center;
            align-items: center;
            text-align: center;
            font-size: #{font-size.$lg};
            font-family: #{font-family.$primary-bold};
            color: var(--grid-item-color-text);

            web-icon {
                --icon-height: #{size.$icon-lg};
                --icon-width: #{size.$icon-lg};
            }
        }
    }

    [data-menu-name] {
        display: flex;
        box-sizing: border-box;
        flex-direction: column;
        gap: var(--menu-levels-gap);
        background-color: var(--background-color);
        opacity: 1;
        height: 100%;
        width: 100%;
        position: absolute;
        overflow-y: auto;
        overflow-x: hidden;
        padding-inline: var(--padding-inline);
        padding-block-start: var(--menu-levels-block-start);
        padding-bottom: var(--overflow-gradient-height);

        web-icon {
            --icon-height: #{size.$icon-lg};
            --icon-width: #{size.$icon-lg};
        }

        &.animate {
            transition: transform var(--slide-speed) ease-in-out;
        }

        &.active {
            transform: translateX(0);
            pointer-events: all;
            transition-delay: 0.1s;
        }

        &.inactive {
            transform: translateX(var(--menu-translate-x));
            pointer-events: none;
        }

        & > web-list {
            --web-list-icon-gap: #{spacing.$lg};
            --web-list-icon-start-size: #{size.$icon-lg};

            a {
                text-decoration: none;
                color: var(--web-list-link-color);
                font-family: #{font-family.$primary-medium};
                font-size: var(--web-list-font-size);

                &:hover {
                    color: var(--web-list-link-hover-color);
                }
            }
        }

        hr {
            margin-block: var(--divider-margin-block);
            border: 0;
            border-top: var(--divider-width) var(--divider-style) var(--divider-color);
        }

        hr + web-list a {
            gap: #{spacing.$sm};
            font-family: #{font-family.$primary-regular};
            font-size: #{font-size.$sm};

            web-icon {
                --icon-height: #{size.$icon-md};
                --icon-width: #{size.$icon-md};
            }
        }

        .grid-item + hr {
            margin-block: #{spacing.$lg};
        }

        web-button {
            .button.button-secondary {
                web-icon {
                    color: #{color.$color-primary};
                }
            }
        }

        .grid-item {
            display: grid;
            gap: 1rem;

            .h4 {
                font-family: #{font-family.$primary-medium};
                font-size: #{font-size.$xl};
                font-weight: var(--web-list-font-weight);
                line-height: #{line-height.$lg};
                margin-block: 0;
                color: var(--grid-item-color-heading);
            }

            p {
                margin-block: 0;
                font-size: #{font-size.$lg};
                line-height: #{line-height.$lg};
                color: var(--grid-item-color-text);
            }

            a.link {
                --color-fill-icon-start: #{action.$color-primary-normal};
                --color-text: var(--grid-item-color-text);
                --link-gap: #{size.$md};
                --link-icon-height: #{size.$icon-md};
                --link-icon-width: #{size.$icon-md};
                font-size: var(--grid-item-link-font-size);
                text-decoration: none;

                &:hover {
                    --color-text: #{color.$color-secondary};
                }

                web-icon {
                    color: color.$color-text-primary;
                    --icon-height: #{size.$icon-md};
                    --icon-width: #{size.$icon-md};
                }
            }

            web-list {
                --web-list-padding-block: 0.6rem;
                a {
                    --color-text: var(--grid-item-color-text);
                    font-size: var(--grid-item-link-font-size);

                    &:hover {
                        --color-text: #{color.$color-secondary};
                    }
                }
            }
        }
    }

    @keyframes slide-in {
        0% {
            opacity: 0;
            transform: translateX(var(--translate-x));
            display: none;
        }

        100% {
            opacity: 1;
            transform: translateX(0);
            display: block;
        }
    }

    @keyframes slide-out {
        0% {
            opacity: 1;
            transform: translateX(0);
            display: block;
        }

        100% {
            opacity: 0;
            transform: translateX(var(--translate-x));
            display: none;
        }
    }

    @keyframes backdrop-fade-in {
        0% {
            background-color: rgba(34, 34, 34, 0);
        }

        100% {
            background-color: rgb(34 34 34 / 25%);
        }
    }
}
